import {createSelector, createSlice} from "@reduxjs/toolkit";
import {getContactGetter, getContactRoleGetter, getOrganizationGetter} from "src/features/entity";
import {VERSION} from "src/version";

const initialState = {
  organizations: [],
  selectedOrganization: null,
  mainMenuOpen: undefined,

  filesById: {},
  pendingFiles: [],

  backendVersion: null,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setOrganizations(state, action) {
      const {organizations} = action.payload;
      organizations?.forEach(organizationId => {
        if (!state.organizations.includes(organizationId)) {
          state.organizations.push(organizationId);
        }
      });
    },
    selectOrganization(state, action) {
      const {organization} = action.payload;
      state.selectedOrganization = organization;
    },
    openMainMenu(state, action) {
      state.mainMenuOpen = true;
    },
    closeMainMenu(state, action) {
      state.mainMenuOpen = false;
    },

    uploadFile(state, action) {
      const {internalId, name, size} = action.payload;
      state.filesById[internalId] = {name, size};
      state.pendingFiles.push(internalId);
    },
    uploadedFile(state, action) {
      const {internalId, auth_token, ...other} = action.payload;
      state.filesById[internalId] = other;
      state.pendingFiles = state.pendingFiles.filter((id) => (id !== internalId));
    },
    uploadFailed(state, action) {
      const {internalId} = action.payload;
      state.filesById[internalId] = {
        ...state.filesById[internalId],
        uploadFailed: true,
      };
    },
    updateFile(state, action) {
      const {internalId, ...other} = action.payload;
      state.filesById[internalId] = {
        ...state.filesById[internalId],
        ...other,
      };
    },
    setBackendVersion(state, action) {
      const {version} = action.payload;
      state.backendVersion = version;
    }
  },
});

export const {
  setOrganizations,
  selectOrganization,
  openMainMenu,
  closeMainMenu,

  uploadFile,
  uploadedFile,
  updateFile,
  uploadFailed,

  setBackendVersion,
} = dashboardSlice.actions;

const reducers = {
  dashboard: dashboardSlice.reducer,
};
export default reducers;

const getDashboardState = (state) => state.dashboard;

export const getMainMenuOpen = createSelector(
  getDashboardState,
  (dashboardState) => dashboardState.mainMenuOpen,
);

export const getSelectedOrganizationId = createSelector(
  getDashboardState,
  (dashboardState) => dashboardState.selectedOrganization,
);

export const getSelectedOrganization = createSelector(
  getSelectedOrganizationId,
  getOrganizationGetter,
  (selectedOrganizationId, organizationGetter) => (organizationGetter(selectedOrganizationId)),
);

export const getRequestUserContactRole = createSelector(
  getSelectedOrganization,
  getContactRoleGetter,
  (selectedOrganization, contactRoleGetter) => {
    const {request_user_contact_role} = selectedOrganization;
    return contactRoleGetter(request_user_contact_role);
  },
);

export const getSessionContactId = createSelector(
  getRequestUserContactRole,
  (requestUserContactRole) => requestUserContactRole?.contact,
);

export const getSessionContact = createSelector(
  getSessionContactId,
  getContactGetter,
  (contactId, contactGetter) => contactGetter(contactId),
);

export const getOrganizationIds = createSelector(
  getDashboardState,
  (dashboardState) => dashboardState.organizations,
);

export const getOrganizations = createSelector(
  getOrganizationIds,
  getOrganizationGetter,
  (organizationIds, organizationGetter) => organizationIds.map(organizationGetter),
);

export const getOrganizationsCount = createSelector(
  getOrganizationIds,
  (organizationIds) => organizationIds.length,
);

const getFilesById = createSelector(
  getDashboardState,
  (dashboardState) => dashboardState.filesById,
);

export const getPendingFileInternalIds = createSelector(
  getDashboardState,
  (dashboardState) => dashboardState.pendingFiles,
);

const defaultFile = {};

export const getPendingFile = createSelector(
  getPendingFileInternalIds,
  getFilesById,
  (pendingFileInternalIds, filesById) => (id) => {
    if (!pendingFileInternalIds.includes(id)) {
      return defaultFile;
    }
    return filesById[id] || defaultFile;
  },
);

export const getVersion = createSelector(
  getDashboardState,
  (dashboardState) => dashboardState.backendVersion || VERSION,
);
